import React, { useEffect, useState, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { fetchNewsById, NewsArticle } from "./NewsAPIUtil";
import { useTranslations } from "src/contexts/TranslationContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SocialShareButtons from "./SocialShareButtons";
import {
    addStructuredData,
    renderMedia,
    processFootnotes,
    components,
    useNewsHreflangTags,
    getLanguageFromPath,
    getPathPrefix,
    SourceSection,
    RelevantNews,
    localizeXImageUrl,
} from "./NewsDetailsHelper";
import { useCanonicalTag } from "src/components/seo/useCanonicalTag";
import ArchiveSection from "./ArchiveSection";
import { NewsArticleJsonLdHelmet, PageTitleDescriptionHelmet } from 'src/components/seo/NewsPageHelmets';

const NewsDetail: React.FC = () => {
    const { lang, id } = useParams<{ [key: string]: string | undefined }>();
    if (!lang || !id) {
        return <div>Error: Missing language or news id in URL</div>;
    }

    const location = useLocation();
    const navigate = useNavigate();

    const { translations: t, language: contextLanguage, getUrlLanguagePrefix } = useTranslations();

    const [news, setNews] = useState<NewsArticle | null>(null);
    const [loading, setLoading] = useState(true);
    const [embedKey, setEmbedKey] = useState(0);
    const [lastLanguage, setLastLanguage] = useState<string | null>(null);
    const { updateHreflangTags } = useNewsHreflangTags(news?.avail_langs || []);

    useCanonicalTag();

    const getCurrentLanguage = useCallback(() => {
        const pathLanguage = getLanguageFromPath();
        return lang || pathLanguage || contextLanguage || "en";
    }, [lang, contextLanguage, getLanguageFromPath]);

    const getTypeTranslation = (type: string | undefined) => {
        if (!type) return "";
        return t?.navigations?.header?.[type.toLowerCase()] || type;
    };

    const loadNews = useCallback(async (newsId: string, newsLang: string) => {
        if (!newsId) return;
        setLoading(true);
        try {
            const newsData = await fetchNewsById(newsId, newsLang);

            if (newsData) {
                newsData.id = newsId;
                if (newsData.title) {
                    newsData.title = newsData.title.replace(/\[\^?\d+\](?:\[\^?\d+\])*/g, "");
                }
                if (newsData.news) {
                    newsData.news = newsData.news.replace(/\[\^?\d+\](?:\[\^?\d+\])*/g, "");
                }

                if (newsData.img_urls && Array.isArray(newsData.img_urls)) {
                    newsData.img_urls = newsData.img_urls.map(localizeXImageUrl);
                  }
            }

            setNews(newsData);
            setLastLanguage(newsLang);
            if (newsData) {
                addStructuredData(newsData);
            }
        } catch (error) {
            console.error("[NewsDetail] Error loading news:", error);
        } finally {
            setLoading(false);
            setEmbedKey((prev) => prev + 1);
        }
    }, []);

    useEffect(() => {
        const currentLang = getCurrentLanguage();
        if (currentLang !== lastLanguage) {
            setLoading(true);
            setLastLanguage(currentLang);
            if (id) {
                loadNews(id, currentLang);
            }
        }
    }, [location.pathname, id, getCurrentLanguage, lastLanguage, loadNews]);

    useEffect(() => {
        if (id && !lastLanguage) {
            const currentLang = getCurrentLanguage();
            loadNews(id, currentLang);
        }
    }, [id, lastLanguage, getCurrentLanguage, loadNews]);

    useEffect(() => {
        // Twitter widgets.js script management
        const existingScript = document.querySelector('script[src="https://platform.twitter.com/widgets.js"]');
        if (existingScript) {
            existingScript.remove();
        }

        const addScript = () => {
            const script = document.createElement("script");
            script.src = "https://platform.twitter.com/widgets.js";
            script.async = true;
            script.onload = () => {
                if (window.twttr?.widgets) {
                    window.twttr.widgets.load();
                }
            };
            document.body.appendChild(script);
        };

        if ("requestIdleCallback" in window) {
            (window as any).requestIdleCallback(addScript);
        } else {
            addScript();
        }

        return () => {
            const scriptToRemove = document.querySelector('script[src="https://platform.twitter.com/widgets.js"]');
            if (scriptToRemove) {
                scriptToRemove.remove();
            }
        };
    }, [embedKey]);

    useEffect(() => {
        if (news?.avail_langs) {
            updateHreflangTags();
        }
    }, [news?.avail_langs, updateHreflangTags]);

    if (loading) return <div className="text-center p-4">Loading...</div>;

    if (!news) {
        return (
            <div>
                <div className="text-center p-4 mb-8">News not found</div>
            </div>
        );
    }

    return (
        <div>
          


            <PageTitleDescriptionHelmet
                title={news.title}
                description={
                    news?.news?.substring(0, 270)
                }
            />


            <NewsArticleJsonLdHelmet
                title={news.title}
                postTime={news.postTime}
                imgUrl={news.img_urls?.[0]}
                body={news.news}
                url={`https://global24.news/en/news/${news.id}`} // or just omit to use window.location.href
            />

            {/* Title & navigation breadcrumbs */}
            <div className="bg-white border-b border-gray-200 py-4 shadow-sm">
                <div className="max-w-7xl mx-auto px-4">
                    <h1 className="text-2xl font-bold capitalize text-gray-800 flex items-center space-x-3">
                        {news.subtype ? (
                            <>
                                <button
                                    onClick={() => {
                                        const prefix = getPathPrefix();
                                        navigate(`/${prefix}/news/list/${news.type}`);
                                    }}
                                    className="hover:text-blue-600 transition-colors"
                                >
                                    {getTypeTranslation(news.type)}
                                </button>
                                <span className="text-gray-400">/</span>
                                <button
                                    onClick={() => {
                                        const prefix = getPathPrefix();
                                        const path = `/${prefix}/news/list/${news.type}/${news.subtype}`;
                                        navigate(path);
                                    }}
                                    className="text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                    {t?.navigations?.header?.[news.subtype?.toLowerCase?.()] || news.subtype}
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => {
                                    const prefix = getPathPrefix();
                                    navigate(`/${prefix}/${news.type}`);
                                }}
                                className="hover:text-blue-600 transition-colors"
                            >
                                {getTypeTranslation(news.type)}
                            </button>
                        )}
                    </h1>
                </div>
            </div>

            {/* Main content container with article tag and schema.org Microdata */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="lg:flex lg:gap-8">
                    <article
                        className="lg:w-3/4"
                        itemScope
                        itemType="https://schema.org/NewsArticle"
                    >
                        <h1
                            className="text-2xl lg:text-3xl font-extrabold text-blue-900 mb-6 line-clamp-5 font-serif tracking-tight leading-tight"
                            itemProp="headline"
                        >
                            {processFootnotes(news.title)}
                        </h1>

                        <div className="flex justify-between items-center text-gray-600 mb-4">
                            <time
                                itemProp="datePublished"
                                dateTime={new Date(news.postTime * 1000).toISOString()}
                            >
                                {new Date(news.postTime * 1000).toLocaleString()}
                            </time>

                            <SocialShareButtons lang={lang} newsId={id} newsText={news.news} />
                        </div>

                        <div className="mb-6">{renderMedia(news)}</div>

                        <div className="prose max-w-none" itemProp="articleBody">
                            <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
                                {news.news}
                            </ReactMarkdown>
                        </div>

                        <SourceSection news={news} />
                    </article>

                    <div className="hidden lg:block lg:w-1/4">
                        <div className="sticky top-4">{/* Ad content will go here */}</div>
                    </div>
                </div>

                <RelevantNews currentNews={news} lang={lang} />

                <ArchiveSection
                    getUrlLanguagePrefix={getUrlLanguagePrefix}
                    language={lang}
                    finalType={news?.type}
                    finalSubtype={news?.subtype}
                    navigate={navigate}
                    t={t}
                />
            </div>
        </div>
    );
};

export default NewsDetail;
